import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  HStack,
  Tooltip,
  Button,
  IconButton,
  useToast,
} from '@chakra-ui/react';
import { useAuth } from 'src/utils/auth';
import { apiRequest } from 'src/utils/fetchUtils';
import { useMarketplaceContext } from 'src/contexts/marketplace';
import { useRouter } from 'next/router';
import { CopyIcon } from '@chakra-ui/icons';

type ImportStatus = 'pending' | 'running' | 'completed' | 'failed';

interface ImportRowData {
  id: string;
  created_at: string;
  updated_at: string;
  status: ImportStatus;
  error_message?: string | null;
  listing_id?: number | null;
  source?: string | null;
  url?: string | null;
}

interface Props {
  isActive?: boolean;
}

const SingleListingImportsTable: React.FC<Props> = ({ isActive = true }) => {
  const { getToken } = useAuth();
  const { marketplace } = useMarketplaceContext();
  const toast = useToast();
  const router = useRouter();
  const [rows, setRows] = useState<ImportRowData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<string>('');

  const deriveRow = (raw: any): ImportRowData => {
    const results: any[] = raw?.results || [];
    let status: ImportStatus = 'pending';
    let error_message: string | null = null;
    let listing_id: number | null = null;
    let source: string | null = null;
    let url: string | null = null;

    for (const r of results) {
      if (typeof r?.listing_id === 'number') listing_id = r.listing_id;
      if (r?.status === 'completed') status = 'completed';
      if (r?.url && !url) url = r.url;
      if (r?.source && !source) source = r.source;
      if (r?.error) {
        status = 'failed';
        const err = String(r.error);
        if (err === 'invalid_url') error_message = 'Invalid URL';
        else if (err === 'invalid_category') error_message = 'Invalid category';
        else if (err === 'invalid_user') error_message = 'Invalid user';
        else if (err === 'scrape_failed') error_message = 'Unable to read listing from URL';
        else error_message = 'Unexpected error';
      }
      if (status === 'pending' && r?.SingleListingImport) status = 'running';
    }

    return {
      id: String(raw.id),
      created_at: raw.created_at,
      updated_at: raw.updated_at,
      status,
      error_message,
      listing_id,
      source,
      url,
    };
  };

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      setError(null);
      const url = `${process.env.NEXT_PUBLIC_API_HOST}/marketplaces/${marketplace?.id}/listings/import/status/`;
      const token = await getToken();
      const response = await apiRequest('GET', url, token);
      const mapped = (response || []).map(deriveRow);
      setRows(mapped);
      setLastUpdate(new Date().toLocaleTimeString());
    } catch (err) {
      setError('Failed to load imports');
    } finally {
      setLoading(false);
    }
  }, [getToken, marketplace?.id]);

  useEffect(() => {
    if (!isActive) return;
    if (!marketplace?.id) return;
    fetchRows();
    const id = setInterval(fetchRows, 8000);
    return () => clearInterval(id);
  }, [fetchRows, isActive, marketplace?.id]);

  const getStatusBadge = (status: ImportStatus) => {
    const statusConfig = {
      pending: { colorScheme: 'gray', label: 'Pending' },
      running: { colorScheme: 'blue', label: 'Running' },
      completed: { colorScheme: 'green', label: 'Completed' },
      failed: { colorScheme: 'red', label: 'Failed' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Badge colorScheme={config.colorScheme} fontSize="sm">
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => new Date(dateString).toLocaleString();

  if (loading && rows.length === 0) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <Spinner size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {error}
        <Button ml={4} size="sm" onClick={fetchRows}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (rows.length === 0) {
    return (
      <Box textAlign="center" py={8}>
        <Text color="gray.500" fontSize="lg">
          No recent imports
        </Text>
        <Text color="gray.400" fontSize="sm" mt={2}>
          Imports started from the Import dialog will be shown here
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <HStack justifyContent="space-between" mb={4}>
        <Text fontSize="sm" color="gray.500">
          {rows.length} import{rows.length === 1 ? '' : 's'}
        </Text>
        <HStack spacing={2}>
          {loading && <Spinner size="sm" />}
          <Text fontSize="sm" color="gray.500">
            Last updated: {lastUpdate}
          </Text>
        </HStack>
      </HStack>

      <Box overflowX="auto">
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>URL</Th>
              <Th>Source</Th>
              <Th>Started</Th>
              <Th>Status</Th>
              <Th>Details</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row) => (
              <Tr key={row.id}>
                <Td>
                  <Text fontFamily="mono" fontSize="sm">
                    #{row.id}
                  </Text>
                </Td>
                <Td maxW="320px">
                  {row.url ? (
                    <HStack>
                      <Tooltip label={row.url}>
                        <Text fontSize="sm" noOfLines={1}>
                          {row.url}
                        </Text>
                      </Tooltip>
                      <Tooltip label="Copy URL">
                        <IconButton
                          aria-label="Copy URL"
                          size="sm"
                          variant="ghost"
                          icon={<CopyIcon />}
                          onClick={() => {
                            navigator.clipboard.writeText(row.url as string);
                            toast({
                              title: 'Copied URL',
                              status: 'success',
                              duration: 1500,
                              isClosable: true,
                              position: 'bottom',
                            });
                          }}
                        />
                      </Tooltip>
                    </HStack>
                  ) : (
                    <Text fontSize="sm" color="gray.400">
                      N/A
                    </Text>
                  )}
                </Td>
                <Td>
                  <Text fontSize="sm">
                    {(row.source && row.source.charAt(0).toUpperCase() + row.source.slice(1)) ||
                      'Other'}
                  </Text>
                </Td>
                <Td>
                  <Tooltip label={formatDate(row.created_at)} placement="top">
                    <Text fontSize="sm">{formatDate(row.created_at)}</Text>
                  </Tooltip>
                </Td>
                <Td>{getStatusBadge(row.status)}</Td>
                <Td>
                  {/* Choosing to purposely not show the error message here since this is end user facing */}
                  {row.status === 'failed' ? (
                    <Text fontSize="sm" color="gray.400">
                      N/A
                    </Text>
                  ) : row.status === 'completed' ? (
                    row.listing_id ? (
                      <Button
                        size="sm"
                        colorScheme="green"
                        onClick={() => router.push(`/listing/edit/${row.listing_id}`)}
                      >
                        Open Listing
                      </Button>
                    ) : (
                      <Text fontSize="sm" color="green.500">
                        Success
                      </Text>
                    )
                  ) : (
                    <Text fontSize="sm" color="gray.400">
                      {row.status === 'running' ? 'In Progress' : 'Waiting'}
                    </Text>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default SingleListingImportsTable;
